import {
  WEEKDAY_KEYS,
  formatMinutesLabel,
  getDayAvailability,
  normalizeWeeklySchedule,
  toDayKey,
} from '../utils';

function DayBadge({ availability }) {
  if (!availability) return null;

  if (availability.type === 'full_day_off') {
    const title = availability.label?.trim() || 'Holiday';
    return (
      <>
        <span
          className="sm:hidden w-1.5 h-1.5 rounded-full bg-[#F37021] shrink-0 mt-1"
          title={title}
        />
        <span
          className="hidden sm:inline-block max-w-full truncate rounded border border-[#F37021] text-[#F37021] px-1 py-0.5 text-[9px] font-medium leading-none bg-white shadow-sm"
          title={title}
        >
          {title}
        </span>
      </>
    );
  }

  if (availability.type === 'closed') {
    return (
      <>
        <span
          className="sm:hidden w-1.5 h-1.5 rounded-full bg-[#F34343] shrink-0 mt-1"
          title="Closed"
        />
        <span className="hidden sm:inline-block max-w-full truncate rounded border border-[#F34343] text-[#F34343] px-1 py-0.5 text-[9px] font-medium leading-none bg-white shadow-sm">
          Closed
        </span>
      </>
    );
  }

  if (availability.type === 'half_day') {
    const title = availability.label?.trim();
    const display =
      title && !['Half day', 'Half Day'].includes(title) ? title : 'Half Day';
    return (
      <>
        <span
          className="sm:hidden w-1.5 h-1.5 rounded-full bg-[#B224EF] shrink-0 mt-1"
          title={display}
        />
        <span
          className="hidden sm:inline-block max-w-full truncate rounded border border-[#B224EF] text-[#B224EF] px-1 py-0.5 text-[9px] font-medium leading-none bg-white shadow-sm"
          title={display}
        >
          {display}
        </span>
      </>
    );
  }

  return null;
}

function shortTime(minutes, compact = false) {
  const label = formatMinutesLabel(minutes).replace(':00', '');
  if (!compact) return label;
  return label.replace(' ', '').replace('AM', 'a').replace('PM', 'p');
}

export function MonthView({
  cells = [],
  eventsByDay = {},
  settings,
  canCreate,
  onDayClick,
  onSlotCreate,
  onEventClick,
  scrollRef,
}) {
  const weekly = normalizeWeeklySchedule(settings?.weekly_schedule);

  return (
    <div className="month-view flex flex-col flex-1 lg:min-h-0 border border-[#EBF4EA] rounded-xl m-1 sm:m-2 lg:m-4 bg-white overflow-hidden">
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-visible lg:overflow-auto custom-scrollbar flex flex-col"
      >
        <div className="w-full min-w-0 flex-1 flex flex-col">
          <div className="grid grid-cols-7 border-b border-[#EBF4EA] shrink-0 bg-white sticky top-0 z-10">
            {WEEKDAY_KEYS.map((key) => {
              const dayType = weekly?.[key]?.type;
              const isClosed = dayType === 'closed';
              return (
                <div
                  key={key}
                  className={`px-0.5 sm:px-2 py-1.5 sm:py-2 text-center text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide flex flex-col items-center justify-center gap-0.5 ${
                    isClosed ? 'text-red-600' : 'text-[#868686]'
                  }`}
                >
                  <span>
                    <span className="sm:hidden">{key.slice(0, 1)}</span>
                    <span className="hidden sm:inline">{key.slice(0, 3)}</span>
                  </span>
                </div>
              );
            })}
          </div>

          <div className="month-grid grid grid-cols-7 grid-rows-6 min-h-[420px] sm:min-h-[480px] lg:min-h-[540px] flex-1">
            {cells.map((cell) => {
              const dayEvents = cell.inMonth ? eventsByDay[cell.key] || [] : [];
              const maxVisible = dayEvents.length <= 2 ? dayEvents.length : 1;
              const visible = dayEvents.slice(0, maxVisible);
              const overflow = dayEvents.length - visible.length;
              const availability = getDayAvailability(cell.key, settings);
              const closed = !availability.available;

              return (
                <div
                  key={cell.key}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    const isPast = cell.key < toDayKey(new Date());
                    if (canCreate && !isPast && cell.inMonth) {
                      onSlotCreate?.(cell.key);
                    } else {
                      onDayClick?.(cell.key);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      const isPast = cell.key < toDayKey(new Date());
                      if (canCreate && !isPast && cell.inMonth) {
                        onSlotCreate?.(cell.key);
                      } else {
                        onDayClick?.(cell.key);
                      }
                    }
                  }}
                  className={`month-cell relative flex flex-col min-h-[70px] sm:min-h-[90px] border-r border-b border-[#EBF4EA] p-1 sm:p-2 text-left transition-colors overflow-hidden ${
                    !cell.inMonth
                      ? 'bg-[#F7F9F7]/80'
                      : 'bg-white hover:bg-[#F5FAF4]'
                  } ${closed ? 'cursor-default' : 'cursor-pointer'}`}
                >
                  <div className="flex items-start justify-between gap-0.5 mb-1 w-full min-w-0">
                    <span
                      className={`inline-flex items-center justify-center font-semibold shrink-0 ${
                        cell.isToday
                          ? 'w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary text-white text-xs sm:text-sm'
                          : !cell.inMonth
                            ? 'text-xs sm:text-base text-[#B0B0B0]'
                            : availability.type === 'full_day_off'
                              ? 'text-xs sm:text-base text-[#F37021]'
                              : availability.type === 'closed'
                                ? 'text-xs sm:text-base text-[#F34343]'
                                : availability.type === 'half_day'
                                  ? 'text-xs sm:text-base text-[#B224EF]'
                                  : 'text-xs sm:text-base text-[#1F2241]'
                      }`}
                    >
                      {cell.day}
                    </span>

                    {cell.inMonth && (
                      <div className="flex-1 min-w-0 flex justify-end">
                        <DayBadge availability={availability} />
                      </div>
                    )}
                  </div>

                  {cell.inMonth && (
                    <div className="flex flex-col mt-auto gap-0.5 relative z-10 w-full">
                      {visible.map((event) => {
                        const styles = {
                          scheduled:
                            'bg-[#FEF5E7] border-[#F1A132] text-[#1F2241]',
                          completed:
                            'bg-[#EBF4EA] border-[#50AB3D] text-[#1F2241]',
                          cancelled:
                            'bg-[#FDECEC] border-[#F34343] text-[#1F2241]',
                        };
                        const eventStyle =
                          styles[event.status] || styles.scheduled;

                        return (
                          <button
                            key={event.id}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEventClick?.(
                                event,
                                e.currentTarget.getBoundingClientRect(),
                              );
                            }}
                            className={`w-full flex items-center justify-center sm:justify-start gap-1 text-left truncate rounded sm:rounded-md px-1 sm:px-2 py-0.5 sm:py-1 text-[8px] sm:text-[11px] font-medium border-l-[2.5px] sm:border-l-[3px] ${eventStyle}`}
                            title={`${event.title} · ${formatMinutesLabel(event.startMinutes)}`}
                          >
                            <span className="sm:hidden font-semibold">
                              {shortTime(event.startMinutes, true)}
                            </span>
                            <span className="hidden sm:inline truncate">
                              {event.title}
                            </span>
                            <span className="hidden sm:inline shrink-0 text-opacity-80">
                              {shortTime(event.startMinutes)}
                            </span>
                          </button>
                        );
                      })}
                      {overflow > 0 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDayClick?.(cell.key);
                          }}
                          className="w-full text-center rounded bg-[#F4F6F8] text-[#50AB3D] py-0.5 sm:py-1 mt-0.5 text-[8px] sm:text-[11px] font-semibold hover:bg-[#EBF4EA] transition-colors"
                        >
                          +{overflow}{' '}
                          <span className="hidden sm:inline">More</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
